import Input from 'components/Common/Form/Input/Input'
import { ProductPaletteContext } from 'contexts/ProductPaletteContext'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import type { InputHTMLAttributes, Ref } from 'react'
import React, { useContext } from 'react'
import styled from 'styled-components'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import LineCheckIcon from '../Icons/line/LineCheckIcon'
import { setSvgIconSize } from '../SvgIcon'

const KNOB_SIZE = 20
const PADDING = 2

const Icon = styled(LineCheckIcon)`
  ${setSvgIconSize(KNOB_SIZE)}
`

const Knob = styled.div`
  width: ${rem(KNOB_SIZE)};
  height: ${rem(KNOB_SIZE)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  pointer-events: none;
  position: absolute;
  top: ${rem(PADDING)};
  left: ${rem(PADDING)};
  transition: 200ms transform, 200ms color;
  display: flex;
  align-items: center;
  justify-content: center;

  > ${Icon} {
    opacity: 0;
    transform: rotate(-45deg);
    transition: 200ms opacity, 200ms transform;
  }
`

const Container = styled.div`
  --unchecked-stroke: transparent;
  --unchecked-background: ${props => props.theme.palette.neutral.default.four};
  --unchecked-hover-background: ${props => props.theme.palette.neutral.default.five};
  --checked-background: ${props => props.theme.palette.brand.primary.normal};
  --checked-hover-background: ${props => props.theme.palette.brand.primary.hover};
  flex-shrink: 0;
  position: relative;
  height: ${rem(24)};
  width: ${rem(KNOB_SIZE * 2)};
  border-radius: ${props => props.theme.borderRadius.round};
  box-shadow: inset 0 0 0 ${rem(1)} var(--unchecked-stroke);
  background-color: var(--unchecked-background);
  color: var(--unchecked-background);
  transition: 200ms background-color, 200ms box-shadow;

  &:has(:checked) {
    background-color: var(--checked-background);
    color: var(--checked-background);
    box-shadow: inset 0 0 0 ${rem(1)} var(--checked-stroke);
  }

  &:not(:has(:disabled)) {
    &:has(:invalid) {
      --unchecked-background: ${props => props.theme.palette.messaging.critical.lightBackground};
      --unchecked-stroke: ${props => props.theme.palette.messaging.critical.normalBackground};
      --checked-background: ${props => props.theme.palette.messaging.critical.lightBackground};
      --checked-stroke: ${props => props.theme.palette.messaging.critical.normalBackground};
    }

    ${mediaHoverable} {
      &:hover {
        background-color: var(--unchecked-hover-background);
        color: var(--unchecked-hover-background);

        &:has(:checked) {
          background-color: var(--checked-hover-background);
          color: var(--checked-hover-background);
        }
      }
    }

    &:focus-within {
      ${interactiveFocusStyle}
    }
  }

  &:has(:disabled) {
    cursor: not-allowed;
    background-color: ${props => props.theme.palette.neutral.default.five};
    color: ${props => props.theme.palette.neutral.default.five};

    &:has(:checked) {
      background-color: ${props => props.theme.palette.neutral.default.five};
      color: ${props => props.theme.palette.neutral.default.five};
    }
  }

  &:has(:checked) > ${Knob} {
    transform: translateX(${rem(KNOB_SIZE - (PADDING * 2))});

    > ${Icon} {
      opacity: 1;
      transform: rotate(0);
    }
  }

  &.product-palette-lux-plus {
    --checked-background: ${props => props.theme.palette.product.luxPlus.background};
    --checked-hover-background: ${props => props.theme.palette.product.luxPlus.hover};
  }

  &.product-palette-tier-bronze {
    --checked-background: ${props => props.theme.palette.product.loyalty.bronze.darkForeground};
    --checked-hover-background: ${props => props.theme.palette.product.loyalty.bronze.darkForeground};
    --checked-stroke: ${props => props.theme.palette.product.loyalty.bronze.darkBackground};
    --unchecked-background: ${props => props.theme.palette.product.loyalty.bronze.normalBackground};
    --unchecked-hover-background: ${props => props.theme.palette.product.loyalty.bronze.normalForeground};
    --unchecked-stroke: ${props => props.theme.palette.product.loyalty.bronze.darkForeground};
  }

  &.product-palette-tier-silver {
    --checked-background: ${props => props.theme.palette.product.loyalty.silver.darkForeground};
    --checked-hover-background: ${props => props.theme.palette.product.loyalty.silver.darkForeground};
    --checked-stroke: ${props => props.theme.palette.product.loyalty.silver.darkBackground};
    --unchecked-background: ${props => props.theme.palette.product.loyalty.silver.normalBackground};
    --unchecked-hover-background: ${props => props.theme.palette.product.loyalty.silver.normalForeground};
    --unchecked-stroke: ${props => props.theme.palette.product.loyalty.silver.darkForeground};
  }

  &.product-palette-tier-gold {
    --checked-background: ${props => props.theme.palette.product.loyalty.gold.darkForeground};
    --checked-hover-background: ${props => props.theme.palette.product.loyalty.gold.darkForeground};
    --checked-stroke: ${props => props.theme.palette.product.loyalty.gold.darkBackground};
    --unchecked-background: ${props => props.theme.palette.product.loyalty.gold.normalBackground};
    --unchecked-hover-background: ${props => props.theme.palette.product.loyalty.gold.normalForeground};
    --unchecked-stroke: ${props => props.theme.palette.product.loyalty.gold.darkForeground};
  }

  &.product-palette-tier-platinum {
    --checked-background: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
    --checked-hover-background: ${props => props.theme.palette.product.loyalty.platinum.darkBackground};
    --checked-stroke: ${props => props.theme.palette.product.loyalty.platinum.darkForeground};
    --unchecked-background: ${props => props.theme.palette.product.loyalty.platinum.lightBackground};
    --unchecked-hover-background: ${props => props.theme.palette.product.loyalty.platinum.lightBackground};
    --unchecked-stroke: ${props => props.theme.palette.product.loyalty.platinum.darkForeground};
  }
`

const InputElement = styled(Input)`
  appearance: none;
  width: 100%;
  height: 100%;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size' | 'children' | 'className' | 'ref'> {
  ref?: Ref<HTMLInputElement>
}

function Switch(props: Props) {
  const productPalette = useContext(ProductPaletteContext)

  return <Container className={productPalette ? `product-palette-${productPalette}` : undefined}>
    <InputElement {...props} type="checkbox" />
    <Knob role="none">
      <Icon />
    </Knob>
  </Container>
}

export default Switch
